
import React, { useState } from 'react';
import { Employee } from '@/utils/mockData';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { departments } from '@/utils/mockData';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

interface EmployeeFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (employee: Employee) => void;
  employee?: Employee;
}

const emptyEmployee: Employee = {
  id: '',
  name: '',
  email: '',
  phone: '',
  position: '',
  department: '',
  availability: {
    monday: { available: false },
    tuesday: { available: false },
    wednesday: { available: false },
    thursday: { available: false },
    friday: { available: false },
    saturday: { available: false },
    sunday: { available: false },
  },
  preferences: {
    maxHoursPerWeek: 40,
    consecutiveDays: 5,
  },
};

const DAYS = [
  { day: 'monday', label: 'Maandag' },
  { day: 'tuesday', label: 'Dinsdag' },
  { day: 'wednesday', label: 'Woensdag' },
  { day: 'thursday', label: 'Donderdag' },
  { day: 'friday', label: 'Vrijdag' },
  { day: 'saturday', label: 'Zaterdag' },
  { day: 'sunday', label: 'Zondag' },
] as const;

const EmployeeForm: React.FC<EmployeeFormProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  employee 
}) => {
  const [formData, setFormData] = useState<Employee>(employee || { ...emptyEmployee, id: Date.now().toString() });
  const [activeTab, setActiveTab] = useState("info");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAvailabilityChange = (day: keyof typeof formData.availability, available: boolean) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: { ...prev.availability[day], available },
      },
    }));
  };

  const handleHoursChange = (
    day: keyof typeof formData.availability,
    field: 'start' | 'end',
    value: string
  ) => {
    const currentHours = formData.availability[day].hours || [9, 17];
    const hourIndex = field === 'start' ? 0 : 1;
    const newHours = [...currentHours] as [number, number];
    newHours[hourIndex] = parseInt(value);

    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: { 
          ...prev.availability[day], 
          available: true,
          hours: newHours,
        },
      },
    }));
  };

  const handlePreferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [name]: parseInt(value),
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.department) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    
    onSave(formData);
    toast.success(employee ? 'Medewerker bijgewerkt' : 'Medewerker toegevoegd');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {employee ? 'Medewerker bewerken' : 'Nieuwe medewerker toevoegen'}
          </DialogTitle>
          <DialogDescription>
            Vul de gegevens in van de medewerker. Klik op opslaan wanneer je klaar bent.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="info" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="info">Informatie</TabsTrigger>
            <TabsTrigger value="availability">Beschikbaarheid</TabsTrigger>
            <TabsTrigger value="preferences">Voorkeuren</TabsTrigger>
          </TabsList>
          
          <form onSubmit={handleSubmit}>
            <TabsContent value="info" className="space-y-4">
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Naam</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Volledige naam"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@voorbeeld.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefoonnummer</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+31 6 12345678"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="position">Functie</Label>
                    <Input
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      placeholder="Functietitel"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Afdeling</Label>
                    <Select
                      value={formData.department}
                      onValueChange={(value) => handleSelectChange('department', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer afdeling" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.name}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="mt-6 flex justify-between">
                <Button type="button" variant="outline" onClick={onClose}>
                  Annuleren
                </Button>
                <Button type="button" onClick={() => setActiveTab("availability")}>
                  Volgende
                </Button>
              </DialogFooter>
            </TabsContent>
            
            <TabsContent value="availability" className="space-y-4">
              <div className="space-y-4">
                {DAYS.map(({ day, label }) => (
                  <div key={day} className="flex items-center justify-between p-2 rounded-md border">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`avail-${day}`}
                        checked={formData.availability[day].available}
                        onCheckedChange={(checked) => 
                          handleAvailabilityChange(day, checked as boolean)
                        }
                      />
                      <Label htmlFor={`avail-${day}`} className="font-medium">
                        {label}
                      </Label>
                    </div>
                    
                    {formData.availability[day].available && (
                      <div className="flex items-center space-x-2">
                        <Select
                          value={String(formData.availability[day].hours?.[0] || 9)}
                          onValueChange={(value) => handleHoursChange(day, 'start', value)}
                        >
                          <SelectTrigger className="w-[80px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 14 }, (_, i) => i + 8).map(hour => (
                              <SelectItem key={`start-${hour}`} value={hour.toString()}>
                                {hour}:00
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span>tot</span>
                        <Select
                          value={String(formData.availability[day].hours?.[1] || 17)}
                          onValueChange={(value) => handleHoursChange(day, 'end', value)}
                        >
                          <SelectTrigger className="w-[80px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 16 }, (_, i) => i + 9).map(hour => (
                              <SelectItem key={`end-${hour}`} value={hour.toString()}>
                                {hour}:00
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <DialogFooter className="mt-6 flex justify-between">
                <Button type="button" onClick={() => setActiveTab("info")}>
                  Terug
                </Button>
                <Button type="button" onClick={() => setActiveTab("preferences")}>
                  Volgende
                </Button>
              </DialogFooter>
            </TabsContent>
            
            <TabsContent value="preferences" className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="maxHoursPerWeek">Maximum uren per week</Label>
                  <Input
                    id="maxHoursPerWeek"
                    name="maxHoursPerWeek"
                    type="number"
                    min="0"
                    max="80"
                    value={formData.preferences.maxHoursPerWeek}
                    onChange={handlePreferenceChange}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="consecutiveDays">Maximum aantal dagen achter elkaar</Label>
                  <Input
                    id="consecutiveDays"
                    name="consecutiveDays"
                    type="number"
                    min="1"
                    max="7"
                    value={formData.preferences.consecutiveDays}
                    onChange={handlePreferenceChange}
                  />
                </div>
              </div>
              
              <DialogFooter className="mt-6 flex justify-between">
                <Button type="button" onClick={() => setActiveTab("availability")}>
                  Terug
                </Button>
                <Button type="submit">Opslaan</Button>
              </DialogFooter>
            </TabsContent>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeForm;
